import {
  createAsyncThunk,
  createSelector,
  createSlice,
  PayloadAction,
} from "@reduxjs/toolkit";

import * as api from "../api";
import { RootState } from "../store";

interface SelectItem {
  label: string;
  value: string;
}

type User = {
  login: string;
  name: string;
  surname: string;
};

type Config = {
  apiEndpoint: string;
  apiPDFEndpoint: string;
  apiPostalCodesAndModelsEndpoint: string;
};

export type FormState = {
  countries: SelectItem[];
  user: User;
  config: Config;
};

const initialState: FormState = {
  countries: [],
  config: {
    apiEndpoint: "",
    apiPDFEndpoint: "",
    apiPostalCodesAndModelsEndpoint: "",
  },
  user: {
    login: "",
    name: "",
    surname: "",
  },
};

export const loadAppData = createAsyncThunk<FormState>(
  "appData/fetch",
  async () => {
    const data = await api.loadAppData();
    return data;
  }
);

const appData = createSlice({
  name: "appData",
  initialState,
  reducers: {
    setUserData(state, action: PayloadAction<User>) {
      state.user = action.payload;
    },
    setCountries(state, action: PayloadAction<SelectItem[] | undefined>) {
      state.countries = Array.isArray(action.payload)
        ? action.payload
        : state.countries;
    },
    setConfig(state, action: PayloadAction<Partial<Config> | undefined>) {
      state.config = { ...state.config, ...(action.payload ?? {}) };
    },
  },
  extraReducers: (builder) => {
    builder.addCase(loadAppData.fulfilled, (state, { payload }) => {
      if (Array.isArray(payload?.countries)) {
        state.countries = payload.countries;
      }
      if (payload?.user) {
        state.user = { ...state.user, ...payload.user };
      }
      if (payload?.config) {
        state.config = { ...state.config, ...payload.config };
      }
    });
  },
});

const selectState = (state: RootState) => state.appData;

export const { setUserData, setCountries, setConfig } = appData.actions;

export const selectCountries = createSelector(selectState, (state) =>
  Array.isArray(state.countries) ? state.countries : []
);

export const selectUserData = createSelector(
  selectState,
  (state) => state.user
);

export const selectLoggedUser = createSelector(
  selectState,
  (state) => state.user.login
);

export const selectApiConfig = createSelector(
  selectState,
  (state) => state.config
);

export const { reducer } = appData;
